import React, { useState, useEffect, useRef } from 'react';
import { Text, View, StyleSheet, TouchableOpacity, Modal, Alert, TextInput } from 'react-native';
import { Camera, CameraType, CameraView } from 'expo-camera';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { Button, ScrollView, SizableText, YStack, XStack, H4, Card, Spinner } from 'tamagui';

import globalStyles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { PinchGestureHandler, State } from 'react-native-gesture-handler';
import { AI_API_URL } from '@api/constant';
import { Platform } from 'react-native';
import * as Haptics from 'expo-haptics';
import DeviceInfo from 'react-native-device-info';
import { verifyOCR } from '@api/aiApi';

// Optional import for device-only MLKit module
let TextRecognition: any = null;
try {
  TextRecognition = require('@react-native-ml-kit/text-recognition').default;
} catch (e) {
  console.warn('MLKit TextRecognition not available on simulator');
}


const FLASH_MODE = {
  "on": 2,
  "off": 1
}

const HandleBarCode = ({ data, type, saveCallback }) => {
  const [hasPermission, setHasPermission] = useState(null);
  const [scanned, setScanned] = useState(false);
  const [facing, setFacing] = useState<CameraType>('back');
  const [progress, setProgress] = useState(0);
  const cameraRef = useRef(null);
  const [identifiedText, setIdentifiedText] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [boundingBoxes, setBoundingBoxes] = useState([]);
  // Set initial zoom to a moderate value for a better default view
  const [zoom, setZoom] = useState(0.15);
  const [focusSquare, setFocusSquare] = useState({ visible: false, x: 0, y: 0 });
  const [aiLoading, setAILoading] = useState(false);
  const [flashMode, setFlashMode] = useState(false);
  const [touchFocusEnabled, setTouchFocusEnabled] = useState(false);

  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [countdown, setCountdown] = useState(20);
  const countdownRef = useRef(null);

  async function validateOCR(text: string, dataSchema: any) {
    setAILoading(true);
    try {
      const res = await verifyOCR({ text, schema: dataSchema });

      // Fix: Access data directly from Axios response
      let data = res.data;

      console.log('OCR Validation Result:', data);

      // Handle the response data
      let parsedObject;

      if (typeof data === 'object' && data !== null) {
        // If data is already an object, use it directly
        parsedObject = data;
      } else if (typeof data === 'string') {
        try {
          // If data is a string, try to parse it as JSON
          parsedObject = JSON.parse(data);
        } catch (parseError) {
          // If it's not valid JSON, try to extract from raw string
          const rawString = data;
          const cleanedRawString = rawString.replace(/(.*\{.*\}.*)/, (match) => {
            const objectContent = match.match(/\{.*\}/);
            return objectContent ? objectContent[0] : match;
          });
          parsedObject = JSON.parse(cleanedRawString);
        }
      } else if (data.raw) {
        // Handle the case where data has a raw property (legacy format)
        const rawString = data.raw;
        const cleanedRawString = rawString.replace(/(.*\{.*\}.*)/, (match) => {
          const objectContent = match.match(/\{.*\}/);
          return objectContent ? objectContent[0] : match;
        });
        parsedObject = JSON.parse(cleanedRawString);
      }

      if (parsedObject && typeof parsedObject === 'object') {
        // Show confirmation modal
        setParsedData(parsedObject);
        setEditableData({ ...parsedObject }); // Create editable copy
        setShowConfirmModal(true);
        startCountdown();

        // Create a readable text summary for display
        const readableText = Object.entries(parsedObject)
          .filter(([key, value]) => value && value !== "")
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');

        setIdentifiedText(readableText || 'Data extracted successfully');
      } else {
        throw new Error('No valid data structure found');
      }

    } catch (error) {
      console.error('OCR API Error:', error);
      Alert.alert('OCR Error', `Failed to process the extracted data. Please try again.\nError: ${error.message}`);
      setIdentifiedText('Failed to extract data');
    } finally {
      setAILoading(false);
    }
  }

  // Countdown and modal functions
  const startCountdown = () => {
    setCountdown(20);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleAutoSave();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleAutoSave = () => {
    if (editableData) {
      // Add haptic feedback
      if (Platform.OS !== 'web') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }

      saveCallback(editableData);
      setShowConfirmModal(false);
      setParsedData(null);
      setEditableData(null);

      // Show success message
      Alert.alert('Success', 'Data saved successfully!');
    }
  };

  const handleManualSave = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    handleAutoSave();
  };

  const handleCancel = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    setShowConfirmModal(false);
    setParsedData(null);
    setEditableData(null);
    setCountdown(20);
  };

  // Handle field value changes
  const handleFieldChange = (key: string, value: string) => {
    setEditableData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  async function handleProcessImage() {
    if (!TextRecognition) {
      Alert.alert('Not Available', 'Text recognition is only available on physical devices, not in the simulator.');
      return;
    }

    const image = await cameraRef.current.takePictureAsync();
    cameraRef.current.pausePreview();

    const { uri } = image;

    try {
      // Use Google ML Kit for text recognition
      const result = await TextRecognition.recognize(uri);
      setIdentifiedText(result.text); // Store the recognized text
      // Store bounding boxes of the text
      const boxes = result.blocks.map(block => ({
        x: block.frame.left,
        y: block.frame.top,
        width: block.frame.width,
        height: block.frame.height,
      }));
      setBoundingBoxes(boxes); // Set bounding boxes for overlay
      validateOCR(result.text, data);
    } catch (err) {
      console.error('OCR Error:', err);
    }
    cameraRef.current.resumePreview();
  }

  // // Event listener hook
  // useEventListener('onProgressChange', (p) => {
  //   setProgress(p.percent / 100);
  // });

  function toggleCameraFacing() {
    setFacing(current => (current === 'back' ? 'front' : 'back'));
  }

  function toggleFlashlight() {
    setFlashMode(current => !current);
  }

  function toggleTouchFocus() {
    setTouchFocusEnabled(current => !current);
  }

  const handleBarCodeScanned = ({ type, data }) => {
    setScanned(true);
    alert(`Bar code with type ${type} and data ${data} has been scanned!`);
  };

  useEffect(() => {
    if (isRefreshing) {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  // Function to handle pinch gesture
  const handlePinch = (event: any) => {
    const scale = event.nativeEvent.scale;
    //Ensure zoom is between 0 and 1
    if (scale > 1) {
      console.log('Zooming in', Math.min(1, zoom + 0.01));
      setZoom((currentZoom) => Math.min(1, currentZoom + 0.01));
    } else {
      console.log('Zooming out', Math.max(0, zoom - 0.01));
      setZoom((currentZoom) => Math.max(0, currentZoom - 0.01));
    }
  };

  // Function to handle touch events
  const handleTouch = (event: any) => {
    const { locationX, locationY } = event.nativeEvent;
    setFocusSquare({ visible: true, x: locationX, y: locationY });

    // Hide the square after 1 second
    setTimeout(() => {
      setFocusSquare((prevState) => ({ ...prevState, visible: false }));
    }, 1000);

    setIsRefreshing(true);
  };

  // Requesting for camera permission
  useEffect(() => {
    (async () => {
      const { status } = await Camera.getCameraPermissionsAsync();
      setHasPermission(status === 'granted');
    })();

  }, []);

  if (hasPermission === null) {
    return <Text style={{ textAlign: "center" }}>Requesting for camera permission...</Text>;
  }
  if (hasPermission === false) {
    return (
      <YStack flex={1}>
        <Text style={{ textAlign: "center" }}>No access to camera</Text>
        <Button onPress={() => {
          getCameraPermission();
        }}>Grant Access</Button>
      </YStack>
    )
  }

  function getCameraPermission() {
    Camera.requestCameraPermissionsAsync().then(({ status }) => {
      setHasPermission(status === 'granted');
    }
    );
  }

  return (
    <View style={{ flex: 1, gap: 20, paddingBottom: 150 }}>
      <PinchGestureHandler
        onGestureEvent={handlePinch}
        onHandlerStateChange={(event) => {
          if (event.nativeEvent.state === State.END) {
            const scale = event.nativeEvent.scale;
            setZoom((currentZoom) => scale * currentZoom);
          }
        }}
      >
        {/*
          Add a key prop to CameraView that changes with zoom or facing to force re-render and apply zoom immediately
        */}
        <CameraView
          key={`camera-${facing}-${zoom}`}
          animateShutter
          autofocus='on'
          ref={cameraRef}
          zoom={zoom}
          style={styles.camera}
          enableTorch={flashMode}
          facing={facing}
          onTouchStart={touchFocusEnabled ? handleTouch : undefined}
        >
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={styles.button} onPress={toggleCameraFacing}>
              <AntDesign name="camerao" size={24} color="white" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.button} onPress={toggleFlashlight}>
              <MaterialIcons
                name={flashMode ? "flash-on" : "flash-off"}
                size={24}
                color={flashMode ? "yellow" : "white"}
              />
            </TouchableOpacity>
            <TouchableOpacity style={styles.button} onPress={toggleTouchFocus}>
              <MaterialIcons
                name={touchFocusEnabled ? "touch-app" : "pan-tool"}
                size={24}
                color={touchFocusEnabled ? "lightgreen" : "white"}
              />
            </TouchableOpacity>
          </View>

          {/* Central recognition rectangle overlay */}
          <View pointerEvents="none" style={styles.centerRectOverlay} />

          {/* Overlay bounding boxes */}
          {boundingBoxes.map((box, index) => (
            <View
              key={index}
              style={[
                styles.boundingBox,
                {
                  top: box.y,
                  left: box.x,
                  width: box.width,
                  height: box.height,
                },
              ]}
            />
          ))}

          {/* Focus square indicator */}
          {focusSquare.visible && touchFocusEnabled && (
            <View
              style={[
                styles.focusSquare,
                {
                  left: focusSquare.x - 25,
                  top: focusSquare.y - 25,
                },
              ]}
            />
          )}

          <View style={[globalStyles.centerEverything, { flexDirection: "row", position: "absolute", bottom: 10, gap: 20 }]}>
            <Button style={[globalStyles.centerEverything]}>
              Import
            </Button>
            <Button
              onPress={handleProcessImage}
              style={[globalStyles.centerEverything]}
              disabled={aiLoading}
              backgroundColor={aiLoading ? '$grey7' : '$blue9'}
            >
              {aiLoading ? 'Processing...' : 'Scan'}
            </Button>
          </View>
        </CameraView>
      </PinchGestureHandler>
      <Animatable.View transition={'flex'} style={{ flex: identifiedText ? 2 : 0.1, minHeight: "20%" }}>
        {identifiedText && aiLoading && (
          <Spinner />
        )}
      </Animatable.View>

      {/* Confirmation Modal */}
      <Modal
        visible={showConfirmModal}
        transparent={true}
        animationType="slide"
        onRequestClose={handleCancel}
      >
        <View style={styles.modalOverlay}>
          <Card style={styles.modalContainer}>
            <YStack space="$4" padding="$4">
              <XStack justifyContent="space-between" alignItems="center">
                <XStack alignItems="center" space="$2">
                  <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
                  <H4>Schema Match Found</H4>
                </XStack>
                <TouchableOpacity onPress={handleCancel} style={styles.closeButton}>
                  <MaterialIcons name="close" size={24} color="#666" />
                </TouchableOpacity>
              </XStack>

              <View style={styles.countdownContainer}>
                <MaterialIcons
                  name="schedule"
                  size={20}
                  color="#2196F3"
                  style={{ marginBottom: 5 }}
                />
                <Text style={styles.countdownText}>
                  Auto-saving in {countdown} second{countdown !== 1 ? 's' : ''}...
                </Text>
                <View style={styles.countdownBar}>
                  <View
                    style={[
                      styles.countdownProgress,
                      { width: `${(countdown / 20) * 100}%` }
                    ]}
                  />
                </View>
              </View>

              <YStack space="$2">
                <Text style={styles.sectionTitle}>AI OCR Match Result:</Text>
                <Text style={[styles.dataText, { fontSize: 12, color: '#666', fontStyle: 'italic', marginBottom: 8 }]}>
                  AI has matched the scanned data to your schema format. You can edit any field below.
                </Text>
                <ScrollView style={styles.dataContainer}>
                  {editableData ? (
                    <YStack space="$3">
                      {Object.entries(editableData).map(([key, value]) => {
                        // Skip empty values for display but keep them for editing
                        return (
                          <YStack key={key} space="$1">
                            <Text style={[styles.dataText, { fontWeight: 'bold', color: '#2196F3', fontSize: 13 }]}>
                              {key}:
                            </Text>
                            <TextInput
                              style={styles.textInput}
                              value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '')}
                              onChangeText={(text) => handleFieldChange(key, text)}
                              multiline={typeof value === 'object' || String(value).length > 50}
                              placeholder={`Enter ${key}...`}
                              placeholderTextColor="#999"
                            />
                          </YStack>
                        );
                      })}
                    </YStack>
                  ) : (
                    <Text style={styles.dataText}>No data extracted</Text>
                  )}
                </ScrollView>
              </YStack>

              <XStack space="$3" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onPress={handleCancel}
                  backgroundColor="transparent"
                  borderColor="$red9"
                  color="$red9"
                >
                  Cancel
                </Button>
                <Button
                  onPress={handleManualSave}
                  backgroundColor="$blue9"
                  color="white"
                >
                  Save Now
                </Button>
              </XStack>
            </YStack>
          </Card>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  camera: {
    flex: 1,
    minHeight: "80%",
    borderRadius: 20,
    overflow: "hidden"
  },
  buttonContainer: {
    flex: 1,
    borderRadius: 20,
    width: 160,
    padding: 10,
    flexDirection: 'row',
    backgroundColor: 'rgb(90,192,234)',
    top: 20,
    right: 20,
    position: "absolute",
  },
  button: {
    flex: 1,
    alignSelf: 'flex-end',
    alignItems: 'center',
    marginHorizontal: 2,
  },
  text: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  boundingBox: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: 'red',
    backgroundColor: 'rgba(255, 0, 0, 0.3)', // Semi-transparent background
  },
  focusSquare: {
    position: 'absolute',
    width: 50,
    height: 50,
    borderWidth: 2,
    borderColor: 'lightgreen',
    backgroundColor: 'rgba(144, 238, 144, 0.3)',
    borderRadius: 25,
    zIndex: 15,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 15,
    width: '90%',
    maxWidth: 400,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  closeButton: {
    padding: 5,
  },
  countdownContainer: {
    alignItems: 'center',
    marginVertical: 10,
  },
  countdownText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 10,
  },
  countdownBar: {
    width: '100%',
    height: 4,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    overflow: 'hidden',
  },
  countdownProgress: {
    height: '100%',
    backgroundColor: '#2196F3',
    borderRadius: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  dataContainer: {
    maxHeight: 200,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 10,
  },
  dataText: {
    fontSize: 14,
    color: '#333',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 10,
    backgroundColor: '#fff',
    fontSize: 14,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    color: '#333',
    minHeight: 40,
    textAlignVertical: 'top',
  },
  centerRectOverlay: {
    position: 'absolute',
    top: '25%',
    left: '10%',
    width: '80%',
    height: '50%',
    borderWidth: 3,
    borderColor: '#00e0ff',
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.08)',
    zIndex: 10,
  },
});

export default HandleBarCode;
